<template>
  <div class="container-fluid text-align-left">
    <div class="row">
      <div v-if="google" class="google-2fa">
        <button
          v-if="google.enabled"
          type="button"
          class="btn button"
          :disabled="loading"
          @click="onDisableGoogleClick"
        >
          Disable Google 2FA
        </button>
        <button v-else type="button" class="btn button" :disabled="loading" @click="onEnableGoogleClick">
          Enable Google 2FA
        </button>

        <div v-if="google.enabled && google.verified">
          <button v-if="google.primary" type="button" class="btn btn-success" disabled>
            <span class="glyphicon glyphicon-ok"></span> Primary
          </button>
          <button v-else type="button" class="btn btn-info" :disabled="loading" @click="onMakeGooglePrimaryClick">
            Make primary
          </button>

          <p>Google 2FA is enabled</p>
        </div>

        <div v-if="google.enabled && !google.verified && google.qr_code && google.secret" class="mt-3">
          <form class="form-horizontal">
            <div class="form-group">
              <label class="col-sm-2 control-label">QR</label>
              <div class="col-sm-10">
                <img :src="google.qr_code" alt="QR" />
              </div>
            </div>
            <div class="form-group">
              <label class="col-sm-2 control-label">Code</label>
              <div class="col-sm-10">
                <p class="form-control-static">{{ google.secret }}</p>
              </div>
            </div>
            <div class="form-group">
              <label for="google-verify-code" class="col-sm-2 control-label">Verify code</label>
              <div class="col-sm-10">
                <input
                  type="text"
                  class="form-control"
                  id="google-verify-code"
                  placeholder="CODE"
                  ref="googleCodeInput"
                  v-model="googleCode"
                  @keypress.enter.prevent="onVerifyGoogle"
                />
              </div>
            </div>

            <button type="button" class="btn button" :disabled="loading" @click="onVerifyGoogle">Verify</button>
          </form>
        </div>
      </div>

      <hr />

      <div v-if="sms" class="sms-2fa">
        <button v-if="sms.enabled" type="button" class="btn button" :disabled="loading" @click="onDisableSmsClick">
          Disable SMS 2FA
        </button>
        <button v-else type="button" class="btn button" :disabled="loading" @click="onEnableSmsClick">
          Enable SMS 2FA
        </button>

        <div v-if="sms.enabled && sms.verified">
          <button v-if="sms.primary" type="button" class="btn btn-success" disabled>
            <span class="glyphicon glyphicon-ok"></span> Primary
          </button>
          <button v-else type="button" class="btn btn-info" :disabled="loading" @click="onMakeSmsPrimaryClick">
            Make primary
          </button>

          <p>SMS 2FA is enabled</p>
        </div>

        <div v-if="sms.enabled" class="mt-3">
          <div v-if="!sms.verified">
            <form class="form-horizontal">
              <div class="form-group">
                <label class="col-sm-2 control-label">Phone number</label>
                <div class="col-sm-10">
                  <vue-tel-input
                    v-model="smsPhoneNumber"
                    v-bind="phoneNumberInputProps"
                    ref="phoneNumberInput"
                  ></vue-tel-input>
                </div>
              </div>

              <button
                type="button"
                class="btn button"
                @click="onInitiateSmsClick"
                :disabled="loading || smsPhoneNumber.length < 10 || sms.phone"
              >
                Initiate Verification Process
              </button>

              <div class="form-group mt-3">
                <label for="sms-verify-code" class="col-sm-2 control-label">Verify code</label>
                <div class="col-sm-10">
                  <input
                    type="text"
                    class="form-control"
                    id="sms-verify-code"
                    placeholder="CODE"
                    ref="smsCodeInput"
                    v-model="smsVerificationCode"
                    @keypress.enter.prevent="onVerifySms"
                  />
                </div>
              </div>

              <button type="button" class="btn button" @click="onVerifySms" :disabled="loading || !sms.phone">
                Verify
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import swal from 'sweetalert2';
import {VueTelInput} from 'vue-tel-input';
import {formatErrorResponse} from '../utility/api/formatErrorResponse';
import {disableGoogleMfa} from '../utility/api/mfa/disableGoogleMfa';
import {disableSmsMfa} from '../utility/api/mfa/disableSmsMfa';
import {enableGoogleMfa} from '../utility/api/mfa/enableGoogleMfa';
import {enableSmsMfa} from '../utility/api/mfa/enableSmsMfa';
import {getAllMfaProviders} from '../utility/api/mfa/getAllMfaProviders';
import {getGoogleMfaSecret} from '../utility/api/mfa/getGoogleMfaSecret';
import {initiateSmsMfa} from '../utility/api/mfa/initiateSmsMfa';
import {setPrimaryMfaProvider} from '../utility/api/mfa/setPrimaryMfaProvider';
import {verifyGoogleMfa} from '../utility/api/mfa/verifyGoogleMfa';
import {verifySmsMfa} from '../utility/api/mfa/verifySmsMfa';
import {showProfileLockedWarning} from '../utility/showProfileLockedWarning';
import * as Sentry from "@sentry/vue";

export default {
  name: 'account-two-factor-authentication-page',
  components: {
    VueTelInput,
  },
  props: {
    profileLocked: {
      type: Boolean,
      required: true,
    },
    profileApproved: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      google: {},
      googleCode: '',
      loading: false,
      sms: {},
      smsPhoneNumber: '',
      smsVerificationCode: '',
      phoneNumberInputProps: {
        mode: 'international',
        disabledFetchingCountry: false,
        disabled: false,
        disabledFormatting: false,
        placeholder: 'Enter a phone number',
        required: true,
        enabledCountryCode: true,
        enabledFlags: true,
        onlyCountries: [],
        ignoredCountries: [],
        autocomplete: 'off',
        dropdownOptions: {
          disabledDialCode: false,
        },
        inputOptions: {
          showDialCode: false,
        },
      },
    };
  },
  async mounted() {
    this.refreshProviders();
  },
  methods: {
    async refreshProviders() {
      const providers = await getAllMfaProviders();

      const google = providers.find(({provider}) => provider === 'google');
      this.google = {...google};
      this.googleCode = '';

      if (google && google.enabled && !google.verified) {
        getGoogleMfaSecret().then(({qr_code, secret}) => (this.google = {...this.google, qr_code, secret}));
      }

      const sms = providers.find(({provider}) => provider === 'sms');
      this.sms = {...sms};
      this.smsPhoneNumber = '';
      this.smsVerificationCode = '';
    },
    async enableGoogle() {
      const {qr_code, secret} = await enableGoogleMfa();

      await this.refreshProviders();

      this.google = {...this.google, qr_code, secret};
    },
    async onDisableGoogleClick() {
      this.loading = true;
      try {
        await disableGoogleMfa();
        await this.refreshProviders();
      } finally {
        this.loading = false;
      }
    },
    async onDisableSmsClick() {
      this.loading = true;
      try {
        await disableSmsMfa();
        await this.refreshProviders();
      } finally {
        this.loading = false;
      }
    },
    onEnableGoogleClick() {
      showProfileLockedWarning.warning(this.profileLocked).then(async () => {
        this.loading = true;
        try {
          await this.enableGoogle();

          this.$nextTick(() => this.$refs.googleCodeInput.focus());
        } finally {
          this.loading = false;
        }
      });
    },
    onEnableSmsClick() {
      showProfileLockedWarning.warning(this.profileLocked).then(async () => {
        this.loading = true;
        try {
          await enableSmsMfa();
          await this.refreshProviders();

          this.$nextTick(() => this.$refs.phoneNumberInput.focus());
        } finally {
          this.loading = false;
        }
      });
    },
    onVerifyGoogle() {
      showProfileLockedWarning.warning(this.profileLocked).then(async () => {
        this.loading = true;
        try {
          const {verified} = await verifyGoogleMfa(this.googleCode.replace(/ /g, ''));
          if (verified) {
            await this.refreshProviders();
          } else {
            this.showErrorMessage('Wrong Verification Code');
          }
        } catch (error) {
          console.error(error);

          if (error.response && error.response.data) {
            this.showErrorMessage(formatErrorResponse(error.response.data));
          } else {
            Sentry.captureException(error);
            this.showErrorMessage('Unexpected Error Occurred');
          }
        } finally {
          this.loading = false;
        }
      });
    },
    async onMakeGooglePrimaryClick() {
      this.loading = true;
      try {
        await setPrimaryMfaProvider('google');
        await this.refreshProviders();
      } finally {
        this.loading = false;
      }
    },
    async onMakeSmsPrimaryClick() {
      this.loading = true;
      try {
        await setPrimaryMfaProvider('sms');
        await this.refreshProviders();
      } finally {
        this.loading = false;
      }
    },
    onInitiateSmsClick() {
      showProfileLockedWarning.warning(this.profileLocked).then(async () => {
        this.loading = true;
        try {
          const {phone} = await initiateSmsMfa(this.smsPhoneNumber);
          this.sms = {...this.sms, phone};

          swal
            .fire({
              title: 'Sweet!',
              text: 'Verification code sent to ' + this.smsPhoneNumber,
              type: 'success',
            })
            .then(() => {
              this.$nextTick(() => this.$refs.smsCodeInput.focus());
            });
        } catch (error) {
          if (error.response && error.response.data) {
            this.showErrorMessage(formatErrorResponse(error.response.data));
          } else {
            Sentry.captureException(error);
            this.showErrorMessage('Unexpected Error Occurred');
          }
        } finally {
          this.loading = false;
        }
      });
    },
    onVerifySms() {
      showProfileLockedWarning.warning(this.profileLocked).then(async () => {
        this.loading = true;
        try {
          const {verified} = await verifySmsMfa(this.smsVerificationCode);

          if (verified) {
            this.refreshProviders();
          } else {
            this.showErrorMessage('Wrong Verification Code');
          }
        } catch (error) {
          if (error.response && error.response.data) {
            this.showErrorMessage(formatErrorResponse(error.response.data));
          } else {
            Sentry.captureException(error);
            this.showErrorMessage('Unexpected Error Occurred');
          }
        } finally {
          this.loading = false;
        }
      });
    },
    showErrorMessage(text) {
      swal.fire({
        title: 'UhOh!',
        text,
        type: 'error',
        confirmButtonText: 'Ok',
        buttonsStyling: false,
        confirmButtonClass: 'btn btn-lg btn-primary',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.text-align-left {
  text-align: left;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-3 {
  margin-top: 1rem;
}

button:focus {
  color: #ffffff;
}

hr {
  margin-top: 20px;
  margin-bottom: 20px;
}
</style>
